import React, { useState } from 'react';
import {
  LandmineItem,
  CallInsightsAnalysis,
  getScoreBadgeClasses,
  getAdherenceLabelClasses,
  formatAdherenceLabel,
  getPriorityClasses,
  formatCallInsightsTimestamp,
} from './types';
import SentimentRow from './SentimentRow';
import TranscriptBlock from './TranscriptBlock';
import ManagerReviewPanel from './ManagerReviewPanel';

interface LandmineAdherenceSectionProps {
  items: LandmineItem[];
  overall_summary: string;
  sessionId: string;
  managerReview?: CallInsightsAnalysis['manager_review'];
  isAdmin: boolean;
  onReviewSaved: () => void;
  onNavigateToTimestamp?: (ts: string) => void;
}

const LandmineAdherenceSection: React.FC<LandmineAdherenceSectionProps> = ({
  items,
  overall_summary,
  sessionId,
  managerReview,
  isAdmin,
  onReviewSaved,
  onNavigateToTimestamp,
}) => {
  const [expandedCard, setExpandedCard] = useState<number | null>(null);
  const [expandedGuidelines, setExpandedGuidelines] = useState<Set<number>>(new Set());
  const [expandedTranscripts, setExpandedTranscripts] = useState<Set<number>>(new Set());

  const toggleCard = (idx: number) => {
    setExpandedCard((prev) => (prev === idx ? null : idx));
  };

  const toggleGuidelines = (idx: number) => {
    setExpandedGuidelines((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const toggleTranscripts = (idx: number) => {
    setExpandedTranscripts((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  if (!items || items.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        No landmine detections in this session.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {overall_summary && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
          <span className="font-semibold">Overall Summary: </span>
          {overall_summary}
        </div>
      )}

      {items.map((item, idx) => {
        const isOpen = expandedCard === idx;
        const hasGuidelines =
          (item.kb_can_say && item.kb_can_say.length > 0) ||
          (item.kb_cannot_say && item.kb_cannot_say.length > 0) ||
          !!item.kb_quick_tips;

        return (
          <div
            key={idx}
            className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden"
          >
            {/* Clickable header — always visible */}
            <button
              type="button"
              onClick={() => toggleCard(idx)}
              className="w-full flex flex-wrap items-start justify-between gap-2 p-4 text-left hover:bg-gray-50 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 text-sm leading-snug">
                  {item.trigger}
                </p>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded ${getPriorityClasses(item.priority)}`}>
                    {item.priority?.toUpperCase() ?? 'UNKNOWN'} Priority
                  </span>
                  {item.triggered_by && (
                    <span className="text-xs text-gray-500">
                      Triggered by: <span className="font-medium capitalize">{item.triggered_by}</span>
                    </span>
                  )}
                  {item.timestamp && (
                    <span className="text-xs text-gray-400">{formatCallInsightsTimestamp(item.timestamp)}</span>
                  )}
                  {item.timestamp_iso && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onNavigateToTimestamp?.(item.timestamp_iso!); }}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium underline"
                    >
                      View in Timeline &rarr;
                    </button>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span
                  className={`text-sm font-bold px-2.5 py-1 rounded ${getScoreBadgeClasses(item.handling_score)}`}
                >
                  {item.handling_score}/100
                </span>
                <span
                  className={`text-xs font-semibold px-2.5 py-1 rounded ${getAdherenceLabelClasses(item.handling_label)}`}
                >
                  {formatAdherenceLabel(item.handling_label)}
                </span>
                <span className="text-gray-400 text-xs ml-1">{isOpen ? '▲' : '▼'}</span>
              </div>
            </button>

            {/* Collapsible body */}
            {isOpen && (
              <div className="p-4 border-t border-gray-100 space-y-3">
                {item.handling_summary && (
                  <p className="text-sm text-gray-700">{item.handling_summary}</p>
                )}

                {(item.what_worked || item.what_missed || item.what_rep_should_have_said) && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {item.what_worked && (
                      <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded p-2.5 text-sm">
                        <span className="text-green-600 font-bold flex-shrink-0 mt-0.5">&#10003;</span>
                        <p className="text-green-900">{item.what_worked}</p>
                      </div>
                    )}
                    {(item.what_missed || item.what_rep_should_have_said) && (
                      <div className="bg-red-50 border border-red-200 rounded p-2.5 text-sm space-y-2">
                        {item.what_missed && (
                          <div className="flex items-start gap-2">
                            <span className="text-red-600 font-bold flex-shrink-0 mt-0.5">&#10007;</span>
                            <p className="text-red-900">{item.what_missed}</p>
                          </div>
                        )}
                        {item.what_rep_should_have_said && (
                          <div className={item.what_missed ? 'pt-2 border-t border-red-200' : ''}>
                            <p className="text-xs font-semibold text-red-700 uppercase mb-1">Should Have Said</p>
                            <p className="text-red-800">{item.what_rep_should_have_said}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {item.customer_reaction && (
                  <p className="text-sm text-gray-500 italic">
                    Customer reaction: {item.customer_reaction}
                  </p>
                )}

                <SentimentRow
                  before={item.sentiment_at_detection}
                  after={item.sentiment_after}
                  labelBefore="At Detection"
                  labelAfter="After"
                />

                {hasGuidelines && (
                  <div>
                    <button
                      onClick={() => toggleGuidelines(idx)}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium focus:outline-none"
                      type="button"
                    >
                      {expandedGuidelines.has(idx) ? 'Hide Compliance Guidelines' : 'Show Compliance Guidelines'}
                      <span className="ml-1">{expandedGuidelines.has(idx) ? '▲' : '▼'}</span>
                    </button>
                    {expandedGuidelines.has(idx) && (
                      <div className="mt-2 space-y-3">
                        {item.kb_can_say && item.kb_can_say.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-green-700 uppercase mb-1">Can Say</p>
                            <ul className="space-y-1">
                              {item.kb_can_say.map((phrase, i) => (
                                <li key={i} className="text-sm text-green-900 bg-green-50 border border-green-200 rounded px-2 py-1">
                                  {phrase}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {item.kb_cannot_say && item.kb_cannot_say.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-red-700 uppercase mb-1">Cannot Say</p>
                            <ul className="space-y-1">
                              {item.kb_cannot_say.map((phrase, i) => (
                                <li key={i} className="text-sm text-red-900 bg-red-50 border border-red-200 rounded px-2 py-1">
                                  {phrase}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {item.kb_quick_tips && (
                          <div className="bg-gray-50 border border-gray-200 rounded p-3 text-sm text-gray-700">
                            <p className="font-semibold text-gray-800 mb-1">Quick Tips</p>
                            <p>{item.kb_quick_tips}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <TranscriptBlock
                  transcripts={item.relevant_transcripts ?? []}
                  isExpanded={expandedTranscripts.has(idx)}
                  onToggle={() => toggleTranscripts(idx)}
                />
              </div>
            )}
          </div>
        );
      })}

      <ManagerReviewPanel
        sessionId={sessionId}
        currentSection="landmine"
        managerReview={managerReview}
        isAdmin={isAdmin}
        onReviewSaved={onReviewSaved}
      />
    </div>
  );
};

export default LandmineAdherenceSection;
